import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Avatar,
  alpha,
  Paper,
  InputAdornment,
  Button,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Popover,
  Grid,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Close,
  Send,
  AttachFile,
  EmojiEmotions,
  Gif,
  People,
  Image,
  VideoFile,
  InsertDriveFile,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { format, isToday, isYesterday } from 'date-fns';
import chatApi from '../../services/chatApi';
import { ChatMessage, OnlineUser, Emoji, Gif as GifType } from '../../types/Chat';
import EmojiPicker from './EmojiPicker';
import GifPicker from './GIFPicker';
import { useNavigate } from 'react-router-dom';

// Background Image 
import ChatDarkBg from '../../assets/Chat-Dark-bg.jpg';
import ChatLightBg from '../../assets/Chat-Light-bg.jpg';
import Logo from '../../assets/WFHCafe_WObg.png';

interface CommunityChatModalProps {
  open: boolean;
  onClose: () => void;
}

interface TypingUser {
  id: string;
  username: string;
}

const CommunityChatModal: React.FC<CommunityChatModalProps> = ({ open, onClose }) => {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  
  // Width settings
  const chatWidth = isMobile ? '100%' : isTablet ? '85%' : '450px';
  
  const isDark = theme.palette.mode === 'dark';
  const goldColor = '#C4B66F';
  const deepGreen = '#051814';
  
  // WebSocket and API state
  const [ws, setWs] = useState<WebSocket | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [selectedGif, setSelectedGif] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [typingUsers, setTypingUsers] = useState<TypingUser[]>([]);
  const [allUsers, setAllUsers] = useState<OnlineUser[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  
  // UI state
  const [isTyping, setIsTyping] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [mentionAnchor, setMentionAnchor] = useState<null | HTMLElement>(null);
  const [mentionQuery, setMentionQuery] = useState('');
  const [filteredUsers, setFilteredUsers] = useState<OnlineUser[]>([]);
  
  // File upload state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [showFileDialog, setShowFileDialog] = useState(false);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>(null);
  
  // Glass effect styling
  const glassEffect = {
    backdropFilter: 'blur(20px)',
    background: isDark
      ? 'linear-gradient(135deg, rgba(5, 24, 20, 0.98) 0%, rgba(15, 59, 46, 0.98) 100%)'
      : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(250, 250, 250, 0.98) 100%)',
    border: `1px solid ${alpha(isDark ? goldColor : deepGreen, 0.2)}`,
    boxShadow: isDark
      ? `0 25px 50px ${alpha('#000000', 0.5)}`
      : `0 25px 50px ${alpha(deepGreen, 0.15)}`,
  };

  // Get WebSocket URL
  const getWebSocketUrl = useCallback(() => {
    const wsUrl = import.meta.env.VITE_WS_URL || 'ws://localhost:3000';
    return `${wsUrl}/ws/chat`;
  }, []);

  // Initialize WebSocket connection
  const initializeWebSocket = useCallback(() => {
    if (!user || !open || ws) return;

    const wsUrl = getWebSocketUrl();
    console.log('Connecting to WebSocket:', wsUrl);
    
    const websocket = new WebSocket(wsUrl);
    
    websocket.onopen = () => {
      console.log('✅ Connected to chat server');
      setIsConnected(true);
      
      // Send authentication immediately
      websocket.send(JSON.stringify({
        type: 'auth',
        userId: user._id,
      }));
    };
    
    websocket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('📨 WebSocket message:', data.type, data);
        
        switch (data.type) {
          case 'connected':
            console.log('Connection established:', data.message);
            break;
            
          case 'auth_success':
            console.log('✅ Authentication successful');
            break;
            
          case 'message':
          case 'message_sent':
            if (data.message && data.message._id) {
              console.log('New message received:', {
                id: data.message._id,
                content: data.message.content?.substring(0, 50),
                sender: data.message.senderId,
                type: data.message.type
              });
              
              setMessages(prev => {
                // Check if message already exists
                const exists = prev.some(msg => msg._id === data.message._id);
                if (!exists) {
                  console.log('Adding new message to state');
                  return [...prev, data.message];
                } else {
                  console.log('Message already exists in state');
                  return prev;
                }
              });
            } else {
              console.error('Invalid message format:', data);
            }
            break;
            
          case 'history':
            console.log('History received:', data.messages?.length, 'messages');
            if (data.messages && Array.isArray(data.messages)) {
              // Log first message structure for debugging
              if (data.messages.length > 0) {
                console.log('First message in history:', {
                  id: data.messages[0]._id,
                  content: data.messages[0].content,
                  sender: data.messages[0].senderId,
                  type: data.messages[0].type
                });
              }
              setMessages(data.messages);
            }
            break;
            
          case 'typing':
            setTypingUsers(data.users || []);
            break;
            
          case 'error':
            console.error('WebSocket error:', data.message);
            break;
            
          case 'pong':
            // Keep-alive response
            break;
        }
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    };
    
    websocket.onclose = () => {
      console.log('🔌 Disconnected from chat server');
      setIsConnected(false);
      
      // Clear WebSocket reference
      setWs(null);
      
      // Attempt to reconnect after 3 seconds if modal is still open
      if (open && user) {
        reconnectTimeoutRef.current = setTimeout(() => {
          console.log('🔄 Attempting to reconnect...');
          initializeWebSocket();
        }, 3000);
      }
    };
    
    websocket.onerror = (error) => {
      console.error('WebSocket error:', error);
    };
    
    setWs(websocket);
    
    return websocket;
  }, [user, open, getWebSocketUrl]);

  // Fetch initial data
  const fetchInitialData = useCallback(async () => {
    if (!user || !open) return;

    try {
      setIsLoading(true);
      
      // Load online users (optional)
      try {
        const usersResponse = await chatApi.getOnlineUsers();
        if (usersResponse.success) {
          setAllUsers(usersResponse.users);
        }
      } catch (error) {
        console.error('Error fetching online users:', error);
      }
      
    } catch (error) {
      console.error('Error fetching initial chat data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user, open]);

  // WebSocket setup effect
  useEffect(() => {
    let websocket: WebSocket | undefined;
    let pingInterval: NodeJS.Timeout;

    if (open && user) {
      websocket = initializeWebSocket();
      
      if (websocket) {
        // Keep-alive ping interval
        pingInterval = setInterval(() => {
          if (websocket && websocket.readyState === WebSocket.OPEN) {
            websocket.send(JSON.stringify({ type: 'ping' }));
          }
        }, 25000);
      }
    }
    
    return () => {
      clearInterval(pingInterval);
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (websocket) {
        websocket.close();
      }
      setWs(null);
    };
  }, [open, user, initializeWebSocket]);

  // Fetch initial data effect
  useEffect(() => {
    if (open && user) {
      fetchInitialData();
    }
  }, [open, user, fetchInitialData]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Typing indicator
  useEffect(() => {
    if (isTyping && ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({
        type: 'typing',
        isTyping: true,
      }));
      
      typingTimeoutRef.current = setTimeout(() => {
        setIsTyping(false);
      }, 2000);
      
      return () => {
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current);
        }
      };
    }
  }, [isTyping, ws]);

  // Handle message input changes
  const handleMessageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setNewMessage(value);
    
    if (!isTyping && value.trim()) {
      setIsTyping(true);
    }
    
    // Check for mention
    const lastWord = value.split(' ').pop();
    if (lastWord?.startsWith('@')) {
      const query = lastWord.slice(1);
      setMentionQuery(query);
      setMentionAnchor(inputRef.current);
      
      // Filter users for mention
      const filtered = allUsers.filter(user => 
        user.username.toLowerCase().includes(query.toLowerCase())
      );
      setFilteredUsers(filtered);
    } else {
      setMentionAnchor(null);
    }
  };

  // Function to handle user profile click
const handleUserProfileClick = (username: string, event?: React.MouseEvent) => {
  if (event) {
    event.stopPropagation(); // Prevent event bubbling
  }
  if (username) {
    navigate(`/${username}`);
  }
};

  // Send message via WebSocket
  const sendMessage = useCallback(async () => {
    if ((!newMessage.trim() && !selectedGif) || !ws || ws.readyState !== WebSocket.OPEN) {
      console.error('Cannot send message:', { 
        hasMessage: !!newMessage.trim(), 
        hasGif: !!selectedGif,
        wsReady: ws?.readyState 
      });
      return;
    }
    
    try {
      const messageContent = selectedGif ? selectedGif : newMessage.trim();
      const messageType = selectedGif ? 'gif' : 'text';
      
      console.log('Sending message via WebSocket:', { 
        content: messageContent.substring(0, 50), 
        type: messageType 
      });
      
      // Send via WebSocket
      ws.send(JSON.stringify({
        type: 'message',
        content: messageContent,
        textContent: selectedGif && newMessage.trim() ? newMessage.trim() : undefined,
        mentions: [],
        messageType,
      }));
      
      setNewMessage('');
      setSelectedGif(null);
      setIsTyping(false);
      
    } catch (error) {
      console.error('Error sending message:', error);
    }
  }, [newMessage, selectedGif, ws]);

  // Handle file upload to Pinata
  const handleFileUpload = async () => {
    if (!selectedFile || !ws || ws.readyState !== WebSocket.OPEN) return;

    try {
      setIsUploading(true);
      setUploadProgress(0);

      // Determine file type
      const fileType = selectedFile.type.startsWith('image/') 
        ? (selectedFile.type === 'image/gif' ? 'gif' : 'image')
        : selectedFile.type.startsWith('video/') ? 'image' : 'file';

      // Upload to Pinata via REST API
      const response = await chatApi.uploadFile(
        selectedFile,
        newMessage.trim() || undefined,
        (progress) => setUploadProgress(progress)
      );

      if (response.success) {
        console.log('File uploaded successfully:', response);
        
        // Send via WebSocket
        ws.send(JSON.stringify({
          type: 'message',
          content: response.pinataUrl || response.message.content,
          textContent: newMessage.trim() || undefined,
          mentions: [],
          messageType: fileType,
        }));

        // Clear states
        setNewMessage('');
        setSelectedFile(null);
        setFilePreview(null);
        setShowFileDialog(false);
        setUploadProgress(0);
      }
    } catch (error) {
      console.error('Error uploading file:', error);
    } finally {
      setIsUploading(false);
    }
  };

  // Handle file selection
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file size (100MB max)
    if (file.size > 100 * 1024 * 1024) {
      alert('File size must be less than 100MB');
      return;
    }

    setSelectedFile(file);
    
    // Create preview for images/videos
    if (file.type.startsWith('image/') || file.type.startsWith('video/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setFilePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setFilePreview(null);
    }
    
    setShowFileDialog(true);
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Cancel file upload
  const handleCancelFile = () => {
    setSelectedFile(null);
    setFilePreview(null);
    setShowFileDialog(false);
    setUploadProgress(0);
  };

  // Handle key press - FIXED: Prevent Enter from triggering when dialog is open
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      // Only send if file dialog is NOT open
      if (!showFileDialog) {
        if (selectedFile) {
          handleFileUpload();
        } else {
          sendMessage();
        }
      }
    }
  };

  // Handle emoji selection
  const handleEmojiSelect = (emoji: string) => {
    setNewMessage(prev => prev + emoji);
    setShowEmojiPicker(false);
    inputRef.current?.focus();
  };

  // Handle GIF selection
  const handleGifSelect = (gifUrl: string) => {
    setSelectedGif(gifUrl);
    setShowGifPicker(false);
    inputRef.current?.focus();
  };

  // Cancel GIF selection
  const handleCancelGif = () => {
    setSelectedGif(null);
  };

  // Select user for mention
  const handleMentionSelect = (selectedUser: OnlineUser) => {
    const messageParts = newMessage.split(' ');
    messageParts[messageParts.length - 1] = `@${selectedUser.username}`;
    setNewMessage(messageParts.join(' ') + ' ');
    setMentionAnchor(null);
    inputRef.current?.focus();
  };

  // Format time
  const formatTime = (date: Date | string) => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return format(dateObj, 'HH:mm');
  };

  // Format date header
  const formatDateHeader = (date: Date | string) => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    if (isToday(dateObj)) {
      return 'Today';
    } else if (isYesterday(dateObj)) {
      return 'Yesterday';
    } else {
      return format(dateObj, 'MMMM d, yyyy');
    }
  };

  // Group messages by date
  const groupedMessages = messages.reduce((groups: Record<string, ChatMessage[]>, message) => {
    const date = formatDateHeader(message.timestamp);
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(message);
    return groups;
  }, {});

  // Render loading state
  if (isLoading) {
    return (
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          right: open ? 0 : '-100%',
          width: chatWidth,
          height: '100vh',
          zIndex: 1300,
          transition: 'right 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: isDark ? 'rgba(0,0,0,0.9)' : 'rgba(255,255,255,0.9)',
        }}
      >
        <CircularProgress sx={{ color: goldColor }} />
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          right: open ? 0 : '-100%',
          width: chatWidth,
          height: '100vh',
          zIndex: 1300,
          transition: 'right 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Chat Header */}
        <Paper
          elevation={0}
          sx={{
            ...glassEffect,
            borderBottom: `1px solid ${alpha(isDark ? goldColor : deepGreen, 0.1)}`,
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderRadius: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ position: 'relative' }}>
              <Avatar
                sx={{
                  bgcolor: alpha(goldColor, 0.1),
                  width: 40,
                  height: 40,
                }}
              >
                <img
                  src={Logo}
                  alt="Logo"
                  style={{
                    width: '80%',
                    height: '80%',
                    objectFit: 'contain',
                  }}
                />
              </Avatar>
              <Box
                sx={{
                  position: 'absolute',
                  bottom: -2,
                  right: -2,
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  bgcolor: isConnected ? '#4CAF50' : '#f44336',
                  border: `2px solid ${isDark ? deepGreen : '#fff'}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              />
            </Box>
            
            <Box>
              <Typography variant="h6" sx={{ 
                fontWeight: 700,
                color: isDark ? '#fff' : deepGreen,
                lineHeight: 1,
              }}>
                wfh/chat
              </Typography>
              <Typography variant="caption" sx={{ 
                color: isDark ? alpha('#fff', 0.7) : alpha(deepGreen, 0.7),
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}>
                {isConnected ? 'Connected' : 'Connecting...'}
              </Typography>
            </Box>
          </Box>
          
          <IconButton
            onClick={onClose}
            sx={{
              color: isDark ? alpha('#fff', 0.7) : alpha(deepGreen, 0.7),
              '&:hover': {
                color: goldColor,
                backgroundColor: alpha(goldColor, 0.1),
              },
            }}
          >
            <Close />
          </IconButton>
        </Paper>

        {/* Messages Container */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            backgroundImage: `url(${isDark ? ChatDarkBg : ChatLightBg})`,
            backgroundSize: '100% auto',
            p: 2,
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(5,24,20,0.05)',
            },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: alpha(goldColor, 0.3),
              borderRadius: '3px',
            },
          }}
        >
          {Object.entries(groupedMessages).map(([date, dateMessages]) => (
            <Box key={date} sx={{ mb: 3 }}>
              {/* Date Header */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                mb: 2,
                position: 'sticky',
                top: 0,
                zIndex: 1,
              }}>
                <Typography
                  variant="caption"
                  sx={{
                    bgcolor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(5,24,20,0.1)',
                    color: isDark ? alpha('#fff', 0.7) : alpha(deepGreen, 0.7),
                    px: 2,
                    py: 0.5,
                    borderRadius: '12px',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                  }}
                >
                  {date}
                </Typography>
              </Box>
              
              {/* Messages for this date */}
              {dateMessages.map((message, index) => {
                const senderId = message.senderId?._id || message.senderId?._id;
                const isCurrentUser = senderId === user?._id || senderId === user?._id;
                const username = message.senderId?.username || 'Unknown User';
                
                const showAvatar = index === 0 || 
                  (dateMessages[index - 1]?.senderId?._id || dateMessages[index - 1]?.senderId?._id) !== senderId ||
                  new Date(message.timestamp).getTime() - 
                  new Date(dateMessages[index - 1]?.timestamp).getTime() > 5 * 60 * 1000;
                
                const messageType = message.messageType || message.type || 'text';
                const isGifMessage = messageType === 'gif';
                const isImageMessage = messageType === 'image';
                const isFileMessage = messageType === 'file';
                
                return (
                  <Box
                    key={message._id}
                    sx={{
                      display: 'flex',
                      flexDirection: isCurrentUser ? 'row-reverse' : 'row',
                      mb: 1,
                      gap: 1,
                    }}
                  >
                    {/* Avatar */}
                    {showAvatar && !isCurrentUser ? (
  <Avatar
    src={message.senderId?.profilePicture}
    onClick={(e) => handleUserProfileClick(username, e)}
    sx={{
      width: 32,
      height: 32,
      mt: 0.5,
      bgcolor: alpha(goldColor, 0.1),
      color: goldColor,
      fontSize: '0.875rem',
      cursor: 'pointer', 
      transition: 'transform 0.2s ease', 
      '&:hover': {
        transform: 'scale(1.1)', 
      }
    }}
  >
    {username.charAt(0).toUpperCase()}
  </Avatar>
) : (
  <Box sx={{ width: 32 }} />
)}
                    
                    {/* Message Bubble */}
                    <Box sx={{ 
                      maxWidth: '80%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: isCurrentUser ? 'flex-end' : 'flex-start',
                      ml: isCurrentUser ? 'auto' : 0,
                      mr: isCurrentUser ? 0 : 'auto',
                    }}>
                      {showAvatar && !isCurrentUser && (
  <Typography
    variant="caption"
    onClick={(e) => handleUserProfileClick(username, e)}
    sx={{
      color: isDark ? alpha('#fff', 0.7) : alpha(deepGreen, 0.7),
      fontWeight: 600,
      mb: 0.5,
      ml: 1,
      cursor: 'pointer', 
      transition: 'color 0.2s ease', 
      '&:hover': {
        color: goldColor, 
      }
    }}
  >
    {username}
  </Typography>
)}
                      
                      <Paper
                        elevation={0}
                        sx={{
                          bgcolor: isCurrentUser
                            ? alpha(goldColor, isDark ? 0.3 : 0.2) // Increased opacity for better visibility
                            : isDark
                              ? 'rgba(255,255,255,0.2)' // Increased from 0.1 to 0.2
                              : 'rgba(5,24,20,0.1)', // Increased from 0.05 to 0.1
                          color: isDark ? (isCurrentUser ? goldColor : '#fff') : deepGreen,
                          borderRadius: '18px',
                          borderTopLeftRadius: isCurrentUser ? '18px' : '4px',
                          borderTopRightRadius: isCurrentUser ? '4px' : '18px',
                          px: 2,
                          py: 1.5,
                          position: 'relative',
                          border: `1px solid ${alpha(isCurrentUser ? goldColor : (isDark ? '#fff' : deepGreen), 0.15)}`, // Slightly thicker border
                          maxWidth: '100%',
                          backdropFilter: 'blur(10px)', // Added blur effect for glass
                          background: isCurrentUser
                            ? isDark
                              ? 'linear-gradient(135deg, rgba(196, 182, 111, 0.25) 0%, rgba(196, 182, 111, 0.15) 100%)' // Gold gradient for current user
                              : 'linear-gradient(135deg, rgba(196, 182, 111, 0.2) 0%, rgba(196, 182, 111, 0.1) 100%)'
                            : isDark
                              ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0.1) 100%)'
                              : 'linear-gradient(135deg, rgba(5, 24, 20, 0.1) 0%, rgba(5, 24, 20, 0.05) 100%)',
                        }}
                      >
                        {/* Handle GIF messages */}
                        {isGifMessage && message.content && (
                          <Box sx={{ maxWidth: 250, maxHeight: 250, mb: message.textContent ? 1 : 0 }}>
                            <img
                              src={message.content}
                              alt="GIF"
                              style={{
                                width: '100%',
                                height: 'auto',
                                borderRadius: '8px',
                              }}
                              onError={(e) => {
                                console.error('Failed to load GIF:', message.content);
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                          </Box>
                        )}
                        
                        {/* Handle Image messages */}
                        {isImageMessage && message.content && (
                          <Box sx={{ maxWidth: 250, maxHeight: 250, mb: message.textContent ? 1 : 0 }}>
                            <img
                              src={message.content}
                              alt="Image"
                              style={{
                                width: '100%',
                                height: 'auto',
                                maxHeight: 250,
                                objectFit: 'contain',
                                borderRadius: '8px',
                              }}
                              onError={(e) => {
                                console.error('Failed to load image:', message.content);
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                          </Box>
                        )}
                        
                        {/* Handle File messages */}
                        {isFileMessage && (
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1,
                            mb: message.textContent ? 1 : 0,
                            p: 1,
                            bgcolor: alpha(isCurrentUser ? goldColor : (isDark ? '#fff' : deepGreen), 0.1),
                            borderRadius: '8px',
                          }}>
                            <InsertDriveFile sx={{ 
                              color: isCurrentUser ? goldColor : (isDark ? '#fff' : deepGreen),
                              fontSize: 32 
                            }} />
                            <Box>
                              <Typography variant="body2" sx={{ 
                                color: isCurrentUser ? goldColor : (isDark ? '#fff' : deepGreen),
                                fontWeight: 600 
                              }}>
                                File
                              </Typography>
                              <Button
                                variant="text"
                                size="small"
                                href={message.content}
                                target="_blank"
                                sx={{
                                  color: goldColor,
                                  textTransform: 'none',
                                  p: 0,
                                  minWidth: 'auto',
                                }}
                              >
                                Open file
                              </Button>
                            </Box>
                          </Box>
                        )}
                        
                        {/* Show text content */}
                        {(messageType === 'text' || message.textContent) && (
                          <Typography sx={{ 
                            fontSize: '0.95rem',
                            lineHeight: 1.4,
                            wordBreak: 'break-word',
                            whiteSpace: 'pre-wrap',
                            fontWeight: 400, // Ensure text has proper weight
                          }}>
                            {message.textContent || message.content}
                          </Typography>
                        )}
                        
                        {/* Timestamp */}
                        <Typography
                          variant="caption"
                          sx={{
                            color: isDark ? alpha('#fff', 0.6) : alpha(deepGreen, 0.6), // Slightly darker for better visibility
                            display: 'block',
                            textAlign: 'right',
                            mt: 0.5,
                            fontSize: '0.7rem',
                            fontWeight: 500,
                          }}
                        >
                          {formatTime(message.timestamp)}
                        </Typography>
                      </Paper>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ))}
          
          {/* Typing indicator */}
          {typingUsers.length > 0 && (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              mb: 2,
              ml: 1,
            }}>
              <Avatar 
  sx={{ 
    width: 24, 
    height: 24, 
    bgcolor: alpha(goldColor, 0.1),
    cursor: 'pointer', 
    transition: 'transform 0.2s ease', 
    '&:hover': {
      transform: 'scale(1.1)', 
    }
  }}
  onClick={(e) => handleUserProfileClick(typingUsers[0]?.username, e)}
>
  <Typography variant="caption" sx={{ color: goldColor }}>
    {typingUsers[0]?.username?.charAt(0) || 'U'}
  </Typography>
</Avatar>
              <Paper
                sx={{
                  bgcolor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(5,24,20,0.08)', // Increased opacity
                  borderRadius: '18px',
                  borderTopLeftRadius: '4px',
                  px: 2,
                  py: 1.5,
                  backdropFilter: 'blur(10px)',
                }}
              >
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      bgcolor: isDark ? alpha('#fff', 0.7) : alpha(deepGreen, 0.7), // Increased opacity
                      animation: 'pulse 1.4s infinite',
                      animationDelay: '0s',
                    }}
                  />
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      bgcolor: isDark ? alpha('#fff', 0.7) : alpha(deepGreen, 0.7),
                      animation: 'pulse 1.4s infinite',
                      animationDelay: '0.2s',
                    }}
                  />
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      bgcolor: isDark ? alpha('#fff', 0.7) : alpha(deepGreen, 0.7),
                      animation: 'pulse 1.4s infinite',
                      animationDelay: '0.4s',
                    }}
                  />
                </Box>
              </Paper>
            </Box>
          )}
          
          <div ref={messagesEndRef} />
        </Box>

        {/* Selected GIF Preview */}
        {selectedGif && (
          <Paper
            elevation={0}
            sx={{
              bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(5,24,20,0.03)',
              border: `1px solid ${alpha(goldColor, 0.3)}`,
              borderRadius: '12px',
              mx: 2,
              mt: 1,
              p: 2,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 2,
              backdropFilter: 'blur(10px)',
            }}
          >
            <Box sx={{ width: 60, height: 60, borderRadius: '8px', overflow: 'hidden' }}>
              <img
                src={selectedGif}
                alt="Selected GIF"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" sx={{ color: isDark ? alpha('#fff', 0.8) : alpha(deepGreen, 0.8) }}>
                GIF selected
              </Typography>
              <Typography variant="body2" sx={{ color: isDark ? '#fff' : deepGreen, mt: 0.5, fontWeight: 500 }}>
                Add a caption...
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={handleCancelGif}
              sx={{ color: isDark ? alpha('#fff', 0.7) : alpha(deepGreen, 0.7) }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Paper>
        )}

        {/* Message Input */}
        <Paper
          elevation={0}
          sx={{
            ...glassEffect,
            borderTop: `1px solid ${alpha(isDark ? goldColor : deepGreen, 0.1)}`,
            p: 2,
            borderRadius: 0,
            position: 'relative',
          }}
        >
          {/* Typing indicator */}
          {typingUsers.length > 0 && (
            <Typography
              variant="caption"
              sx={{
                color: goldColor,
                display: 'block',
                mb: 1,
                ml: 1,
                fontWeight: 600,
              }}
            >
              {typingUsers.map(u => u.username).join(', ')} {typingUsers.length === 1 ? 'is' : 'are'} typing...
            </Typography>
          )}
          
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
            {/* Attachment buttons */}
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {/* Hidden file input */}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                style={{ display: 'none' }}
                accept="image/*,video/*,.pdf,.doc,.docx,.txt"
              />
              
              <IconButton
                size="small"
                onClick={() => fileInputRef.current?.click()}
                sx={{
                  color: isDark ? alpha('#fff', 0.7) : alpha(deepGreen, 0.7),
                  '&:hover': {
                    color: goldColor,
                    bgcolor: alpha(goldColor, 0.1),
                  },
                }}
              >
                <AttachFile fontSize="small" />
              </IconButton>
              
              <IconButton
                size="small"
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                sx={{
                  color: isDark ? alpha('#fff', 0.7) : alpha(deepGreen, 0.7),
                  '&:hover': {
                    color: goldColor,
                    bgcolor: alpha(goldColor, 0.1),
                  },
                }}
              >
                <EmojiEmotions fontSize="small" />
              </IconButton>
              
              <IconButton
                size="small"
                onClick={() => setShowGifPicker(!showGifPicker)}
                sx={{
                  color: selectedGif ? goldColor : (isDark ? alpha('#fff', 0.7) : alpha(deepGreen, 0.7)),
                  '&:hover': {
                    color: goldColor,
                    bgcolor: alpha(goldColor, 0.1),
                  },
                }}
              >
                <Gif fontSize="small" />
              </IconButton>
            </Box>
            
            {/* Message input */}
            <TextField
              inputRef={inputRef}
              fullWidth
              multiline
              maxRows={4}
              value={newMessage}
              onChange={handleMessageChange}
              onKeyPress={handleKeyPress}
              placeholder={selectedGif ? "Add a caption..." : "Type a message..."}
              variant="outlined"
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '24px',
                  bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(5,24,20,0.02)',
                  borderColor: alpha(isDark ? goldColor : deepGreen, 0.2),
                  '&:hover': {
                    borderColor: goldColor,
                  },
                  '&.Mui-focused': {
                    borderColor: goldColor,
                  },
                },
                '& .MuiInputBase-input': {
                  color: isDark ? '#fff' : deepGreen,
                  fontSize: '0.95rem',
                  '&::placeholder': {
                    color: isDark ? alpha('#fff', 0.5) : alpha(deepGreen, 0.5),
                  },
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={selectedFile ? handleFileUpload : sendMessage}
                      disabled={(!newMessage.trim() && !selectedGif && !selectedFile) || !isConnected}
                      sx={{
                        color: ((newMessage.trim() || selectedGif || selectedFile) && isConnected) ? goldColor : (isDark ? alpha('#fff', 0.3) : alpha(deepGreen, 0.3)),
                        '&:hover': {
                          bgcolor: alpha(goldColor, 0.1),
                        },
                      }}
                    >
                      <Send fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Paper>

        {/* Emoji Picker */}
        {showEmojiPicker && (
          <EmojiPicker
            open={showEmojiPicker}
            onClose={() => setShowEmojiPicker(false)}
            onSelect={handleEmojiSelect}
            isDark={isDark}
          />
        )}

        {/* GIF Picker */}
        {showGifPicker && (
          <GifPicker
            open={showGifPicker}
            onClose={() => setShowGifPicker(false)}
            onSelect={handleGifSelect}
            isDark={isDark}
          />
        )}

        {/* CSS animation for typing indicator */}
        <style>{`
          @keyframes pulse {
            0%, 60%, 100% {
              transform: scale(1);
              opacity: 1;
            }
            30% {
              transform: scale(1.2);
              opacity: 0.7;
            }
          }
        `}</style>
      </Box>

      {/* File Upload Dialog */}
      <Dialog 
        open={showFileDialog} 
        onClose={handleCancelFile}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            overflow: 'hidden',
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: isDark ? deepGreen : '#fff',
          color: isDark ? '#fff' : deepGreen,
          borderBottom: `1px solid ${alpha(isDark ? goldColor : deepGreen, 0.1)}`,
          fontWeight: 600,
        }}>
          Send File
        </DialogTitle>
        <DialogContent sx={{ 
          bgcolor: isDark ? deepGreen : '#fff',
          color: isDark ? '#fff' : deepGreen,
          pt: 3,
        }}>
          {filePreview && (
            <Box sx={{ mb: 2, textAlign: 'center' }}>
              <img
                src={filePreview}
                alt="Preview"
                style={{
                  maxWidth: '100%',
                  maxHeight: 200,
                  borderRadius: '8px',
                }}
              />
            </Box>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            {selectedFile?.type.startsWith('image/') ? (
              <Image sx={{ color: goldColor }} />
            ) : selectedFile?.type.startsWith('video/') ? (
              <VideoFile sx={{ color: goldColor }} />
            ) : (
              <InsertDriveFile sx={{ color: goldColor }} />
            )}
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {selectedFile?.name}
            </Typography>
            <Typography variant="caption" sx={{ ml: 'auto', color: isDark ? alpha('#fff', 0.7) : alpha(deepGreen, 0.7) }}>
              {(selectedFile?.size || 0) / 1024 / 1024 > 1 
                ? `${((selectedFile?.size || 0) / 1024 / 1024).toFixed(1)} MB` 
                : `${Math.round((selectedFile?.size || 0) / 1024)} KB`}
            </Typography>
          </Box>
          <TextField
            fullWidth
            multiline
            rows={2}
            value={newMessage}
            onChange={handleMessageChange}
            placeholder="Add a caption (optional)"
            variant="outlined"
            size="small"
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(5,24,20,0.02)',
                borderColor: alpha(isDark ? goldColor : deepGreen, 0.2),
                '&:hover': {
                  borderColor: goldColor,
                },
              },
              '& .MuiInputBase-input': {
                color: isDark ? '#fff' : deepGreen,
                fontWeight: 400,
              },
            }}
          />
          {isUploading && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress 
                variant="determinate" 
                value={uploadProgress}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  bgcolor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(5,24,20,0.1)',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: goldColor,
                  },
                }}
              />
              <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 1, fontWeight: 500 }}>
                Uploading... {uploadProgress}%
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          bgcolor: isDark ? deepGreen : '#fff',
          borderTop: `1px solid ${alpha(isDark ? goldColor : deepGreen, 0.1)}`,
          p: 2,
        }}>
          <Button
            onClick={handleCancelFile}
            sx={{
              color: isDark ? alpha('#fff', 0.7) : alpha(deepGreen, 0.7),
              fontWeight: 500,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleFileUpload}
            disabled={isUploading}
            variant="contained"
            sx={{
              bgcolor: goldColor,
              color: deepGreen,
              fontWeight: 600,
              borderRadius: '8px',
              px: 3,
              '&:hover': {
                bgcolor: alpha(goldColor, 0.8),
              },
              '&:disabled': {
                bgcolor: alpha(goldColor, 0.3),
                color: alpha(deepGreen, 0.3),
              },
            }}
          >
            {isUploading ? 'Uploading...' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CommunityChatModal;